import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { setChangeRequestOptionOverrides } from '@/services/change-requests';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';
import { ChangeRequest, BuyerOptionGroupOverride, OptionGroupOverrideSelection } from '@/types';
import ChangeRequestOverrideOptionSelect from '../ChangeRequestOverrideOptionSelect';
import styles from './ChangeRequestOverrideOptionDialog.module.scss';

interface ChangeRequestOverrideOptionDialogProps {
    changeRequest: ChangeRequest;
    show: boolean;
    onClose(): void;
    onSave(newOverrides: BuyerOptionGroupOverride[]): void;
}

const ChangeRequestOverrideOptionDialog: React.FC<ChangeRequestOverrideOptionDialogProps> = ({
    show,
    onClose,
    onSave,
    changeRequest,
}) => {
    const [saving, setSaving] = useState(false);
    const [selectedOverrides, setSelectedOverrides] = useState<OptionGroupOverrideSelection[]>([]);

    const handleOpen = () => {
        setSelectedOverrides(changeRequest.optionGroupOverrides.map((x) => ({ optionGroupId: x.optionGroupId, roomId: x.roomId })));
    };

    const handleSaveClick = async () => {
        setSaving(true);

        try {
            const result = await setChangeRequestOptionOverrides(changeRequest.id, selectedOverrides);
            triggerHandler(onSave, result);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleCancelClick();
            }}
            onOpen={handleOpen}
            heightLimit="window">
            <Dialog.Content contentWidth="lg" className={styles.content}>
                <Dialog.Header>
                    <Trans>Edit overrides</Trans>
                </Dialog.Header>
                <ChangeRequestOverrideOptionSelect
                    onChange={setSelectedOverrides}
                    value={selectedOverrides}
                    residentialUnitId={changeRequest.residentialUnitId}
                />
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="primary" onClick={handleSaveClick} loading={saving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default ChangeRequestOverrideOptionDialog;
